import React, { FC } from 'react';
import { Panel } from '@component/Panel';
import { ASSETS_PATH } from '@app/react/const/variables';
import {
    OrderPartsHelpContents,
    Title,
    SubTitle,
    Paragraph,
    Image,
} from './OrderPartsHelpContents';
import { SakuraIcon } from './SakuraIcon';
import { OrderPartsHelpProps } from './type';
import { handleKeyPress } from './handleKeyPress';

const IMG_PATH = `${ASSETS_PATH}/img/order-help`;
const imgPath = {
    shaft: [
        `${IMG_PATH}/shaft/what-shaft.png`,
        `${IMG_PATH}/shaft/shaft-dura.png`,
        `${IMG_PATH}/shaft/shaft-carbon.png`,
    ],
};

const duraShaftData: Array<TableData> = [
    {
        kind: '1913',
        diameter: 7.5,
        weight: 21.5,
        deflection: 18,
    },
    {
        kind: '2014',
        diameter: 8.0,
        weight: 24.1,
        deflection: 15,
    },
    {
        kind: '2015',
        diameter: 8.0,
        weight: 25.6,
        deflection: 14,
    },
];

const carbonShaftData: Array<TableData> = [
    {
        kind: 'EASTONウッド76-20',
        diameter: 7.6,
        weight: 21.4,
        deflection: 15,
    },
    {
        kind: 'EASTONウッド80-23',
        diameter: 8.0,
        weight: 23.0,
        deflection: 13,
    },
    {
        kind: 'EASTON560アルミカーボン',
        diameter: 6.3,
        weight: 20.5,
        deflection: 13,
    },
    {
        kind: 'EASTON65-16（節付竹柄）',
        diameter: 6.5,
        weight: 16.9,
        deflection: 12,
    },
    {
        kind: 'ミズノSST65-15EH（ハード）',
        diameter: 6.5,
        weight: 14.2,
        deflection: 13,
    },
    {
        kind: 'ミズノSST65-16ES（ソフト）',
        diameter: 6.5,
        weight: 14.4,
        deflection: 15,
    },
    {
        kind: 'ミズノSST75-18（エンジ・BM）',
        diameter: 7.5,
        weight: 17.4,
        deflection: 15,
    },
    {
        kind: 'ミズノSST80-20（エンジ・BM）',
        diameter: 8.0,
        weight: 19.4,
        deflection: 12,
    },
    {
        kind: 'ミズノSST80-24（エンジ・BM）',
        diameter: 8.0,
        weight: 22.7,
        deflection: 11,
    },
    {
        kind: 'ミズノSST75-20BC',
        diameter: 7.5,
        weight: 19.3,
        deflection: 17,
    },
    {
        kind: 'ミズノSST80-24BC',
        diameter: 8.0,
        weight: 23.9,
        deflection: 10,
    },
    {
        kind: 'KCカーボン75-22',
        diameter: 7.5,
        weight: 21.6,
        deflection: 13,
    },
    {
        kind: 'KCカーボン80-25',
        diameter: 8.0,
        weight: 23.3,
        deflection: 10,
    },
    {
        kind: 'KC麦粒75-18',
        diameter: 7.5,
        weight: 18.0,
        deflection: 12,
    },
    {
        kind: 'KC麦粒80-23',
        diameter: 8.0,
        weight: 22.0,
        deflection: 8,
    },
    {
        kind: '雷槌七六式（黒・DW・JB）',
        diameter: 7.6,
        weight: 20.6,
        deflection: 13,
    },
    {
        kind: '雷槌八〇式（黒・DW・JB）',
        diameter: 8.0,
        weight: 23.1,
        deflection: 9,
    },
    {
        kind: 'HAYABUSAカーボン7622',
        diameter: 7.6,
        weight: 20.6,
        deflection: 13,
    },
    {
        kind: 'HAYABUSAカーボン8025',
        diameter: 8.0,
        weight: 24.3,
        deflection: 9,
    },
    {
        kind: 'SYAカーボン7522',
        diameter: 7.6,
        weight: 21.3,
        deflection: 10,
    },
    {
        kind: 'SYBカーボン7522',
        diameter: 7.6,
        weight: 21.2,
        deflection: 14,
    },
    {
        kind: '匠カーボン7517',
        diameter: 7.5,
        weight: 17.1,
        deflection: 15,
    },
    {
        kind: '匠カーボン8022',
        diameter: 8.0,
        weight: 20.8,
        deflection: 9,
    },
    {
        kind: 'WENEW6515',
        diameter: 6.5,
        weight: 14.5,
        deflection: 15,
    },
    {
        kind: 'WENEW7518',
        diameter: 7.5,
        weight: 17.1,
        deflection: 14,
    },
    {
        kind: 'WENEW8023',
        diameter: 8.0,
        weight: 23.2,
        deflection: 12,
    },
];

type MillMeter = number;
type Gram = number;
type TableData = {
    kind: string;
    diameter: MillMeter;
    weight: Gram;
    deflection: MillMeter;
};

const TableRow: FC<{ row: TableData }> = ({ row }) => (
    <tr className="table-row">
        <td>{row.kind}</td>
        <td>
            {row.diameter.toFixed(1)}
            mm
        </td>
        <td>
            約
            {row.weight.toFixed(1)}
            g
        </td>
        <td>
            {row.deflection}
            mm
        </td>
    </tr>
);

const ShaftTable: FC<{ data: Array<TableData> }> = ({ data }) => (
    <table className="order-parts-help-table">
        <thead className="header">
            <tr>
                <th>
                    <strong>種類</strong>
                </th>
                <th>
                    <strong>直径</strong>
                </th>
                <th>
                    <strong>重さ</strong>
                    ※シャフトのみ
                    <p className="font-size-10">(100cmあたり)</p>
                </th>
                <th>
                    <strong>たわみ量</strong>
                    <p className="font-size-10">自社計測</p>
                </th>
            </tr>
        </thead>
        <tbody>
            {data.map((row, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <TableRow key={index} row={row} />
            ))}
        </tbody>
    </table>
);

export const ShaftInner: FC<{ className?: string }> = ({ className }) => (
    <OrderPartsHelpContents className={className}>
        <Title>
            <SakuraIcon />
            シャフトの選び方
        </Title>

        <SubTitle>シャフトとは</SubTitle>
        <div>
            <Paragraph>
                「シャフト」とは、下記図の紫色で示している棒状の部分を指します。
            </Paragraph>
            <Image src={imgPath.shaft[0]} />
        </div>

        <SubTitle>選び方</SubTitle>
        <div>
            <Paragraph>
                初めてお作りになられるお客様の場合は、弓の強さを参考にお選びいただくとよろしいかと思います。
                2組目以降の作製のお客様の場合は、
                素材・硬さ・太さ・重心等、色々な組合せをお楽しみいただければと思います。
            </Paragraph>
            <Paragraph>
                矢の重量は通常シャフト重量プラス4～5ｇくらいになります。
                ウェイトや矢尻の種類により、更に増減いたします。
            </Paragraph>
        </div>

        <SubTitle>素材の違い</SubTitle>
        <div>
            <Paragraph>
                大きく分けると、ジュラルミンとカーボンシャフトの2種類があります。
            </Paragraph>
        </div>

        <SubTitle>素材1 ジュラルミン</SubTitle>
        <div>
            <Image src={imgPath.shaft[1]} />
            <Paragraph>
                不均一な竹矢しか無かった時代に開発された安価で均一性の高いシャフトです。
                ジュラルミンシャフトのおかげで、弓道初心者の方でも信頼できる矢を使用する事が可能となり、
                現在も弓道の発展に貢献しています。
                サイズ・カラーも豊富で、特に近的競技においては現在でも多くの方に使用されています。
                曲がりますが、直せる場合もあり、耐久性もあります。
            </Paragraph>
            <div className="overflow-x-scroll">
                <ShaftTable data={duraShaftData} />
            </div>
        </div>

        <SubTitle>素材2 カーボン</SubTitle>
        <div>
            <Image src={imgPath.shaft[2]} />
            <Paragraph>
                高価な竹矢と安価なジュラルミンシャフトの2択の時期が永く続きましたが、
                「より高性能の矢を」「竹矢に近い味わいの矢を」等のお客様の声をもとに開発されたのが
                様々なカーボンシャフトです。
                <br />
                カーボンシャフトの特徴として共通して言われている事が、振動減衰性に優れていること、
                矢飛びが良いこと。金属と違い、竹矢に近い肌触り等があります。
                形状・材質・太さ・硬さ・重さ・価格など様々なお客様のご要望に応えるようになってきています。
            </Paragraph>
            <div className="overflow-x-scroll">
                <ShaftTable data={carbonShaftData} />
            </div>
        </div>
    </OrderPartsHelpContents>
);

export const OrderPartsHelpShaft: FC<OrderPartsHelpProps> = ({
    isOpen,
    handleClose,
}) => (
    <Panel isOpen={isOpen} onRequestClose={handleClose}>
        <div
            className="order-parts-help-panel-close"
            onClick={handleClose}
            onKeyPress={handleKeyPress(handleClose)}
            role="button"
            tabIndex="0"
        >
            <i className="icon-close" />
        </div>
        <div className="order-parts-help-panel">
            <div className="scrollable">
                <ShaftInner />
            </div>
        </div>
    </Panel>
);
